import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
} from "@mui/material";
import axios from "axios";

const riskQuestions = [
  {
    level: "Level 1",
    items: [
      {
        text: "Suicidal ideation? (Since last session if this is a follow-up.)",
        name: "suicidalIdeation",
        hasTextField: true,
      },
    ],
  },
  {
    level: "Level 2",
    items: [
      { text: "Suicide Risk (Ideation)", name: "suicideRisk", hasTextField: true },
      {
        text: "Plan/Method (Has a suicide plan/method)",
        name: "planMethod",
        hasTextField: true,
      },
      { text: "Intent (Suicide intent)", name: "intent", hasTextField: true },
      { text: "Impulse control", name: "impulseControl", hasTextField: true },
      { text: "Agitation", name: "agitation", hasTextField: true },
      {
        text: "Perceived burdensomeness",
        name: "perceivedBurdensomeness",
        hasTextField: true,
      },
      {
        text: "Sleep difficulties (insomnia, nightmares)",
        name: "sleepDifficulties",
        hasTextField: true,
      },
      { text: "Mania/Hypomania", name: "maniaHypomania", hasTextField: true },
      { text: "Psychosis", name: "psychosis", hasTextField: true },
      { text: "Substance abuse", name: "substanceAbuse", hasTextField: true },
      {
        text: "Lifetime history of suicide attempts",
        name: "suicideAttemptsHistory",
        hasTextField: true,
      },
    ],
  },
  {
    level: "Level 3",
    items: [
      {
        text: "Family history of suicide/suicidal behavior",
        name: "familyHistorySuicide",
        hasTextField: true,
      },
      {
        text: "Recent suicide of friend/family member",
        name: "recentSuicide",
        hasTextField: true,
      },
      {
        text: "Recent death of a family member/friend",
        name: "recentDeath",
        hasTextField: true,
      },
      {
        text: "Recent breakup or loss of relationship",
        name: "recentBreakup",
        hasTextField: true,
      },
      {
        text: "Physical illness",
        name: "physicalIllness",
        hasTextField: true,
      },
      { text: "Hopelessness", name: "hopelessness", hasTextField: true },
      { text: "Helplessness", name: "helplessness", hasTextField: true },
      {
        text: "Demographic vulnerability (male, GLBT, int'l student)",
        name: "demographicVulnerability",
        hasTextField: true,
      },
      { text: "Legal/Arrest history", name: "legalHistory", hasTextField: true },
      { text: "Anti-social traits", name: "antisocialTraits", hasTextField: true },
    ],
  },
  {
    level: "Proactive Factors",
    items: [
      {
        text: "Cooperative alliances with a mental health professional",
        name: "cooperativeAlliances",
        hasTextField: true,
      },
      {
        text: "Physiological resilience and/or good problem solving skills",
        name: "physiologicalResilience",
        hasTextField: true,
      },
      {
        text: "Available/accessible social support",
        name: "socialSupport",
        hasTextField: true,
      },
      {
        text: "Intact relationship/young children",
        name: "intactRelationship",
        hasTextField: true,
      },
      {
        text: "Hopefulness/plans for the future",
        name: "hopefulness",
        hasTextField: true,
      },
      {
        text: "Fear of hurting/disappointing others",
        name: "fearHurtingOthers",
        hasTextField: true,
      },
      {
        text: "Religious/moral beliefs",
        name: "religiousBeliefs",
        hasTextField: true,
      },
      {
        text: "Motivated for treatment",
        name: "motivatedTreatment",
        hasTextField: true,
      },
      {
        text: "Fear of legal consequences",
        name: "fearLegalConsequences",
        hasTextField: true,
      },
    ],
  },
  {
    level: "Interventions",
    items: [
      {
        text: "Identified reason for living",
        name: "reasonForLiving",
        hasTextField: true,
      },
      {
        text: "Consultation with psychiatrist",
        name: "consultPsychiatrist",
        hasTextField: true,
      },
      {
        text: "Consultation with clinic director",
        name: "consultClinicDirector",
        hasTextField: true,
      },
      {
        text: "Consultation with crisis counselor",
        name: "consultCrisisCounselor",
        hasTextField: true,
      },
      {
        text: "Consultation (indicate with whom, when and the nature of contact)",
        name: "consultationOther",
        hasTextField: true,
      },
      {
        text: "Worked to reduce perceived burdensomeness",
        name: "reduceBurdensomeness",
        hasTextField: true,
      },
      { text: "Worked to decrease shame", name: "decreaseShame", hasTextField: true },
    ],
  },
  {
    level: "Post-Intervention",
    items: [
      {
        text: "After intervention is hospitalization recommended?",
        name: "hospitalizationRecommended",
        hasTextField: true,
      },
      {
        text: "Did risk level decrease during session?",
        name: "riskLevelDecreased",
        hasTextField: true,
      },
      { text: "Homicidal ideation?", name: "homicidalIdeation", hasTextField: true },
      {
        text: "Assessment of current suicide risk",
        name: "currentSuicideRisk",
        hasTextField: true,
      },
      {
        text: "Assessment of current homicide risk",
        name: "currentHomicideRisk",
        hasTextField: true,
      },
    ],
  },
];

const BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const valueOrDash = (v) => {
  if (Array.isArray(v)) {
    const list = v
      .map((x) => (typeof x === "string" ? x.trim() : x))
      .filter(Boolean);
    return list.length ? list.join(", ") : "—";
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "—";
  const s = String(v);
  return s.trim() === "" ? "—" : s;
};

const splitList = (s) =>
  typeof s === "string"
    ? s
      .split(";")
      .map((x) => x.trim())
      .filter(Boolean)
    : Array.isArray(s)
      ? s.filter(Boolean)
      : [];

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

const parseMaybeDoubleEncoded = (input) => {
  try {
    let out = input;
    if (typeof out === "string") out = JSON.parse(out);
    if (typeof out === "string") out = JSON.parse(out);
    return out;
  } catch {
    return input;
  }
};

const Field = ({ label, value }) => {
  const display = valueOrDash(value);
  return (
    <TextField
      label={label}
      value={display}
      fullWidth
      size="small"
      variant="outlined"
      InputProps={{ readOnly: true }}
      multiline
      minRows={1}
    />
  );
};

const Symptom = ({ label, details, checked }) => {
  const shown = details ?? (checked ? "Yes" : "—");
  return <Field label={label} value={shown} />;
};

const CAGEScreening = ({ note }) => {
  if (!note) {
    return (
      <Typography variant="body2" color="text.secondary">
        No CAGE screening data available.
      </Typography>
    );
  }

  // Map the field names from the API
  const includeCAGE = note?.cage === "Include";
  const cageDate = note?.screeningDate;
  const screeningDetails = note?.screeningDetails;
  const score = note?.score;

  return (
    <>
      <Typography variant="h6" gutterBottom color="primary">
        Screening Details
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}
      >
        CAGE
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 4,
          mb: 1,
        }}
      >
        <Field
          label="Include"
          value={includeCAGE ? "Include" : "Not Include"}
        />
        <Field
          label="Date of Screening"
          value={cageDate ? new Date(cageDate).toLocaleDateString() : "—"}
        />
        <Field
          label="Screening Details"
          value={screeningDetails}
        />
      </Box>

      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}
      >
        Questions
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 4,
          mb: 1.5,
        }}
      >
        <Field
          label="1. Have you ever felt that you ought to cut down on your drinking or drug use?"
          value={note?.question1 || "—"}
        />
        <Field
          label="2. Have people annoyed you by criticizing your drinking or drug use?"
          value={note?.question2 || "—"}
        />
        <Field
          label="3. Have you ever felt bad or guilty about your drinking or drug use?"
          value={note?.question3 || "—"}
        />
        <Field
          label="4. Have you ever had a drink or used drugs first thing in the morning to steady your nerves or to get rid of a hangover?"
          value={note?.question4 || "—"}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 4,
          mb: 1.5,
        }}
      >
        <Field
          label="Score"
          value={score || "—"}
        />
      </Box>
    </>
  );
};

const RiskAssessment = ({ note }) => {
  if (!note) {
    return (
      <Typography variant="body2" color="text.secondary">
        No risk assessment data available.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="h6" gutterBottom color="primary">
        Risk / Safety Assessment & Substance Use
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}
      >
        Risk Assessment Questions
      </Typography>

      {riskQuestions.map((section) => (
        <Box key={section.level} sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}
          >
            {section.level}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 4,
              mb: 1,
            }}
          >
            {section.items.map((item) => {
              const fieldValue = note?.[`${item.name}Details`] || note?.[item.name] || "";

              return (
                <Field
                  key={item.name}
                  label={item.text}
                  value={fieldValue}
                />
              );
            })}
          </Box>
        </Box>
      ))}
    </>
  );
};

const StickyTitleHeader = ({ title, DOS }) => {
  const fmtDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };
  return (
    <Box
      sx={{
        position: "sticky",
        top: "-16px",
        zIndex: (t) => t.zIndex.appBar,
        bgcolor: "#fff",
        p: 4,
        pb: 0,
        mb: 3,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
          maxWidth: { xs: "25%", sm: "30%", md: "auto" },
        }}
      >
        <strong>DOS:</strong> {fmtDate(DOS)}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          position: "absolute",
          top: 16,
          left: { xs: "30%", sm: "35%", md: "50%" },
          transform: { xs: "translateX(0)", md: "translateX(-50%)" },
          fontWeight: "bold",
          fontSize: { xs: "0.85rem", sm: "1rem", md: "1.25rem" },
          maxWidth: { xs: "65%", sm: "60%", md: "50%" },
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title || "Adult Diagnostic Assessment "}
      </Typography>
      <Box sx={{ height: 28 }} />
      <Divider />
    </Box>
  );
};

const phq9Items = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
  "Trouble concentrating on things, such as reading the newspaper or watching television",
  "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
  "Thoughts that you would be better off dead or of hurting yourself in some way",
];

const gad7Items = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid as if something awful might happen",
];

const scoreToFreq = (n) => {
  const v = Number(n);
  if (v === 0) return "Not at all";
  if (v === 1) return "Several days";
  if (v === 2) return "More than half the days";
  if (v === 3) return "Nearly every day";
  return "";
};

const phqSeverity = (t) => {
  const v = Number(t);
  if (v <= 4) return "Minimal";
  if (v <= 9) return "Mild";
  if (v <= 14) return "Moderate";
  if (v <= 19) return "Moderately severe";
  return "Severe";
};

const gadSeverity = (t) => {
  const v = Number(t);
  if (v <= 4) return "Minimal";
  if (v <= 9) return "Mild";
  if (v <= 14) return "Moderate";
  return "Severe";
};

const normalizeSurveyObj = (raw) => {
  if (!raw) return {};
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      obj = {};
    }
  }
  return obj || {};
};

const difficultyFromFlags = (o = {}) => {
  if (o.extremelyDifficult) return "Extremely difficult";
  if (o.vertDifficult || o.veryDifficult) return "Very difficult";
  if (o.someWhatDifiicult || o.somewhatDifficult) return "Somewhat difficult";
  if (o.difficultAll || o.notDifficultAtAll) return "Not difficult at all";
  return "";
};

const headerFieldSx = {
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: "0.9rem",
    padding: "0 10px",
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  "& .MuiSvgIcon-root": { display: "none" },
};

const AdultDiagnosticAssessmentPreview = (props) => {
  const { data, formData } = props || {};
  const patientFormId = props?.patientFormId ?? data?.patientFormId;
  const formTitle = props?.formTitle ?? data?.formTitle;

  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (formData || data) {
      const src = formData ?? data;
      const ad = src?.adultDiagnostic ?? src;
      setNote(ad || null);
      setMeta({
        practiceName:
          pick(src, ["practiceName", "particeName"]) ?? ad?.practiceName ?? "",
        accountNo:
          pick(src, ["accountNo", "fileno", "fileNo"]) ??
          ad?.accountNo ??
          "",
        assessmentDate:
          pick(src, ["assessmentDate", "asssessmentDate"]) ??
          ad?.assessmentDate ??
          ad?.asssessmentDate ??
          "",
        patientName:
          pick(src, ["patientName", "PatientNameclient", "clientName"]) ??
          ad?.patientName ??
          ad?.clientName ??
          "",
        dob: pick(src, ["dob", "dOB", "DOB"]) ?? ad?.dob ?? ad?.dOB ?? "",
        customFormTitle: src.customFormTitle ?? ad?.customFormTitle ?? "",
        providerDate: src.providerDate ?? ad?.providerDate ?? "",
        supervisorSignatureDATE:
          src.supervisorSignatureDATE ?? ad?.supervisorSignatureDATE ?? "",
        staffsignatureDATE:
          src.staffsignatureDATE ?? ad?.staffsignatureDATE ?? "",
        supervisedDate: src.supervisedDate ?? ad?.supervisedDate ?? "",
        supervisedClinicianMsg:
          pick(src, [
            "messagesupervisedClinician",
            "messageSupervisedClinician",
            "supervisedClinicianMessage",
          ]) ??
          pick(ad || {}, [
            "messagesupervisedClinician",
            "messageSupervisedClinician",
            "supervisedClinicianMessage",
          ]),
        internMsg:
          pick(src, [
            "messageinternSign",
            "messageInternSign",
            "internMessage",
          ]) ??
          pick(ad || {}, [
            "messageinternSign",
            "messageInternSign",
            "internMessage",
          ]),
        providerMsg:
          pick(src, [
            "message",
            "messageProviderSignature",
            "messageProfessionalSignature",
            "messageProviderSign",
            "massagestaffSignature",
          ]) ??
          pick(ad || {}, [
            "message",
            "messageProviderSignature",
            "messageProfessionalSignature",
            "messageProviderSign",
            "massagestaffSignature",
          ]),
        supervisorMsg:
          pick(src, [
            "messageSupervisorSignature",
            "messageSupervisorSign",
            "supervisorMessage",
          ]) ??
          pick(ad || {}, [
            "messageSupervisorSignature",
            "messageSupervisorSign",
            "supervisorMessage",
          ]),
      });
    }
  }, [formData, data]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!patientFormId) return;
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");

        const client = axios.create({
          baseURL: BASE,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/json",
          },
        });

        const [pfRes, adRes] = await Promise.all([
          client.get(`/Forms/GetPatientFormsbyId?Id=${patientFormId}`),
          client.get(
            `/AdultDiagnosticAssessment/AdultDiagnosticAssesment?id=${patientFormId}`,
          ),
        ]);

        if (!mounted) return;

        const pfParsed = parseMaybeDoubleEncoded(pfRes.data);
        const pf = Array.isArray(pfParsed) ? pfParsed[0] || {} : pfParsed || {};

        const adParsed = parseMaybeDoubleEncoded(adRes.data);
        const adultDiagnostic = adParsed?.adultDiagnostic || adParsed || null;

        setMeta({
          practiceName: pf.practiceName,
          accountNo: pf.accountNo,
          assessmentDate: pf.assessmentDate,
          patientName: pf.patientName,
          dob: pf.dob,
          customFormTitle: pf.customFormTitle,
          providerDate: pf.providerDate,
          supervisorSignatureDATE: pf.supervisorSignatureDATE,
          staffsignatureDATE: pf.staffsignatureDATE,
          supervisedDate: pf.supervisedDate,
          supervisedClinicianMsg:
            pick(pf, [
              "messagesupervisedClinician",
              "messageSupervisedClinician",
              "supervisedClinicianMessage",
            ]) ??
            pick(adultDiagnostic || {}, [
              "messagesupervisedClinician",
              "messageSupervisedClinician",
              "supervisedClinicianMessage",
            ]),
          internMsg:
            pick(pf, [
              "messageinternSign",
              "messageInternSign",
              "internMessage",
            ]) ??
            pick(adultDiagnostic || {}, [
              "messageinternSign",
              "messageInternSign",
              "internMessage",
            ]),
          providerMsg:
            pick(pf, [
              "message",
              "messageProviderSignature",
              "messageProfessionalSignature",
              "messageProviderSign",
              "massagestaffSignature",
            ]) ??
            pick(adultDiagnostic || {}, [
              "message",
              "messageProviderSignature",
              "messageProfessionalSignature",
              "messageProviderSign",
              "massagestaffSignature",
            ]),
          supervisorMsg:
            pick(pf, [
              "messageSupervisorSignature",
              "messageSupervisorSign",
              "supervisorMessage",
            ]) ??
            pick(adultDiagnostic || {}, [
              "messageSupervisorSignature",
              "messageSupervisorSign",
              "supervisorMessage",
            ]),
        });
        setNote(adultDiagnostic);
      } catch (e) {
        if (mounted) setError("Failed to load preview.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [patientFormId]);

  const DOS = useMemo(
    () =>
      pick(meta ?? note ?? {}, ["assessmentDate", "asssessmentDate"]) || "",
    [meta, note],
  );
  const patient = useMemo(
    () =>
      pick(meta ?? note ?? {}, [
        "patientName",
        "PatientNameclient",
        "clientName",
      ]) || "",
    [meta, note],
  );
  const dob = useMemo(
    () => pick(meta ?? note ?? {}, ["dob", "dOB", "DOB"]) || "",
    [meta, note],
  );
  const accountNo = useMemo(
    () => pick(meta ?? note ?? {}, ["accountNo", "fileno", "fileNo"]) || "",
    [meta, note],
  );

  const phqObj = useMemo(() => normalizeSurveyObj(note?.patientPHQ9), [note]);
  const gadObj = useMemo(() => normalizeSurveyObj(note?.patientGAD7), [note]);

  const phqScores = useMemo(() => {
    const arr = Array.from({ length: 9 }, (_, i) =>
      Number(phqObj[`q${i + 1}`] ?? 0),
    );
    const sum =
      phqObj.totalScore != null
        ? Number(phqObj.totalScore)
        : arr.reduce((a, b) => a + b, 0);
    return {
      arr,
      total: sum,
      diff: difficultyFromFlags(phqObj),
      severity: phqSeverity(sum),
    };
  }, [phqObj]);

  const gadScores = useMemo(() => {
    const arr = Array.from({ length: 7 }, (_, i) =>
      Number(gadObj[`q${i + 1}`] ?? 0),
    );
    const sum =
      gadObj.totalScore != null
        ? Number(gadObj.totalScore)
        : arr.reduce((a, b) => a + b, 0);
    return {
      arr,
      total: sum,
      diff: difficultyFromFlags(gadObj),
      severity: gadSeverity(sum),
    };
  }, [gadObj]);

  const fmtDate = (val) => {
    const v = valueOrDash(val);
    if (v === "—") return v;
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString();
  };

  const fmtTime = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const sigDisplay = (msg, dateVal) => {
    const m = String(msg || "").trim();
    if (m) return m;
    const d = fmtDate(dateVal);
    return d !== "—" ? `Signed on ${d}` : "Pending signature";
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !note) {
    return (
      <Typography variant="body2" color="text.secondary" align="center">
        {error || "No data available for preview."}
      </Typography>
    );
  }

  const titleDisplay = meta?.customFormTitle || note.customFormTitle;
  const dosDisplay = fmtDate(DOS);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
      }}
    >
      <StickyTitleHeader
        title={
          formTitle ||
          meta?.customFormTitle ||
          note.customFormTitle ||
          "Adult Diagnostic Assessment"
        }
        DOS={DOS}
      />

      <Box sx={{ px: 4 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {valueOrDash(accountNo)}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client:</strong> {valueOrDash(patient)}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {fmtDate(dob)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Diagnostic Assessment
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <TextField
            label="Title"
            value={titleDisplay || "—"}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={headerFieldSx}
          />
          <TextField
            label="Assessment Date"
            value={dosDisplay || "—"}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={headerFieldSx}
          />
          <TextField
            label="Start Time"
            value={fmtTime(note.startTime)}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={headerFieldSx}
          />
          <TextField
            label="End Time"
            value={fmtTime(note.endTime)}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={headerFieldSx}
          />
          <TextField
            label="Date of Assessment Interviews"
            value={fmtDate(note.assessmentInterviewDate)}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={headerFieldSx}
          />
        </Box>

        {/* Contributions to the Assessment - Full Width and Extendable */}
        <Box sx={{ mb: 1, mt: 2 }}>
          <Field
            label="Contributions to the Assessment"
            value={note.contributional}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Current Life Situation
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field label="Age" value={note.age} />
          <Field label="Living situation" value={note.livingSituation} />
          <Field
            label="Basic needs and economic status"
            value={note.basicNeed}
          />
          <Field label="Education" value={note.education} />
          <Field
            label="Significant Personal Relationships"
            value={note.significiant}
          />
          <Field label="Strengths and resources" value={note.strengths} />
          <Field label="Belief systems" value={note.beleifs} />
          <Field
            label="Contextual non-personal factors"
            value={note.contextual}
          />
          <Field
            label="General physical health and relationship to client's culture"
            value={note.phisicalHealth}
          />
          <Field label="Current medications" value={note.medication} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Presenting Problems & Reason for Referral
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field
            label="Perception of condition"
            value={note.preceptionCondition}
          />
          <Field
            label="Description of symptoms"
            value={note.descriptionSymptoms}
          />
          <Field label="Reason for referral" value={note.referrel} />
          <Field
            label="History of mental health and treatment"
            value={note.history}
          />
          <Field
            label="Important Developmental Incidents"
            value={note.importantIncident}
          />
          <Field label="Trauma history" value={note.tramaHistory} />
          <Field
            label="Maltreatment or abuse issues"
            value={note.malTreatment}
          />
          <Field
            label="History of alcohol and drug usage and treatment"
            value={note.drugHistory}
          />
          <Field label="Health history" value={note.healthHistory} />
          <Field
            label="Family health history"
            value={note.familyHealthHistory}
          />
          <Field
            label="Cultural influences and their impact on the client"
            value={note.cultural}
          />
          <Field label="Communication style" value={note.communication} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Mental Status Exam
        </Typography>

        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
          General Observations
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field label="Appearance" value={note.appearance} />
          <Field label="Build" value={note.build} />
          <Field
            label="Demeanor/Response to Interviewer"
            value={note.demeanor}
          />
          <Field label="Eye Contact" value={note.eyeContact} />
          <Field label="Activity Level" value={note.activityLevel} />
          <Field label="Speech" value={note.speech} />
        </Box>

        <Typography sx={{ fontWeight: 700, my: 0.5 }}>
          Thought Content and Perception
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field
            label="Delusions"
            value={pick(note, ["delusions", "delusionsPTSD"])}
          />
          <Field
            label="Hallucinations"
            value={pick(note, ["hallucinations", "hallucinationsPTSD"])}
          />
          <Field label="Other Hallucination" value={note.otherHallucination} />
        </Box>

        <Typography sx={{ fontWeight: 700, my: 0.5 }}>Self-Danger</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field label="Self-Abuse" value={note.selfAbuse} />
          <Field label="Violence to Others" value={note.violence} />
          <Field
            label="Destruction to Property"
            value={pick(note, ["destruction", "destructionToProperty"])}
          />
        </Box>

        <Typography sx={{ fontWeight: 700, my: 0.5 }}>Cognition</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field label="Orientation" value={note.orientation} />
          <Field label="Memory Issues" value={note.memoryIssues} />
          <Field label="Attention / Concentration" value={note.attention} />
          <Field label="Judgment/Insight" value={note.judgment} />
        </Box>

        <Typography sx={{ fontWeight: 700, my: 0.5 }}>
          Thought and Perceptual Process
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field label="Mood" value={note.mood} />
          <Field label="Affect" value={note.affect} />
          <Field label="Behavior" value={note.behavior} />
          <Field label="Anxiety" value={note.anxiety} />
          <Field
            label="Intelligence Estimate"
            value={note.intelligenceEstimate}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Depression Disorders
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Symptom
            label="Depressed mood most of the day"
            details={note.depressedMood}
            checked={note.depressedMoodCheckbox}
          />
          <Symptom
            label="Diminished interest in activities"
            details={note.diminishedInterest}
            checked={note.diminishedInterestCheckbox}
          />
          <Symptom
            label="Insomnia/hypersomnia"
            details={note.insomnia}
            checked={note.insomniaCheckbox}
          />
          <Symptom
            label="Appetite too much/too little"
            details={note.appetite}
            checked={note.appetiteCheckbox}
          />
          <Symptom
            label="Significant weight loss/gain"
            details={note.weightChange}
            checked={note.weightChangeCheckbox}
          />
          <Symptom
            label="Feelings of worthlessness/guilt"
            details={note.worthlessness}
            checked={note.worthlessnessCheckbox}
          />
          <Symptom
            label="Psychomotor agitation/retardation"
            details={note.psychomotor}
            checked={note.psychomotorCheckbox}
          />
          <Symptom
            label="Fatigue/energy loss"
            details={note.fatigue}
            checked={note.fatigueCheckbox}
          />
          <Symptom
            label="Diminished ability to concentrate/make decisions"
            details={note.concentration}
            checked={note.concentrationCheckbox}
          />
          <Symptom
            label="Recurrent thoughts of death/suicide"
            details={note.suicideThoughts}
            checked={note.suicideThoughtsCheckbox}
          />
          <Symptom
            label="History of self-harm/suicide attempt"
            details={note.selfHarm}
            checked={note.selfHarmCheckbox}
          />
          <Symptom
            label="Other"
            details={note.otherDepression}
            checked={note.otherDepressionCheckbox}
          />
          <Symptom
            label="Low Self Esteem (Dysthymic Disorder Only)"
            details={note.lowSelfEsteem}
            checked={note.lowSelfEsteemCheckbox}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Generalized Anxiety Disorder
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Symptom
            label="Excessive anxiety/worry"
            details={note.excessiveAnxiety}
            checked={note.excessiveAnxietyCheckbox}
          />
          <Symptom
            label="Difficult to control worry"
            details={note.controlWorry}
            checked={note.controlWorryCheckbox}
          />
          <Symptom
            label="Easily fatigued"
            details={note.easilyFatigued}
            checked={note.easilyFatiguedCheckbox}
          />
          <Symptom
            label="Difficulty concentrating/Mind going blank"
            details={note.difficultyConcentrating}
            checked={note.difficultyConcentratingCheckbox}
          />
          <Symptom
            label="Irritability"
            details={note.irritability}
            checked={note.irritabilityCheckbox}
          />
          <Symptom
            label="Restlessness/On edge"
            details={note.restlessness}
            checked={note.restlessnessCheckbox}
          />
          <Symptom
            label="Muscle tension"
            details={note.muscleTension}
            checked={note.muscleTensionCheckbox}
          />
          <Symptom
            label="Disturbed sleep"
            details={note.disturbedSleep}
            checked={note.disturbedSleepCheckbox}
          />
          <Symptom
            label="Other"
            details={note.otherAnxiety}
            checked={note.otherAnxietyCheckbox}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Additional Symptomology & Mania
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Symptom
            label="Panic Attacks"
            details={note.panicAttacks}
            checked={note.panicAttacksCheckbox}
          />
          <Symptom
            label="Agoraphobia"
            details={note.agoraphobia}
            checked={note.agoraphobiaCheckbox}
          />
          <Symptom
            label="Specific Phobia"
            details={note.specificPhobia}
            checked={note.specificPhobiaCheckbox}
          />
          <Symptom
            label="Obsessive Compulsive Disorder"
            details={note.ocd}
            checked={note.ocdCheckbox}
          />
          <Symptom
            label="Inflated self-esteem/grandiosity"
            details={note.selfEsteem}
            checked={note.selfEsteemCheckbox}
          />
          <Symptom
            label="Decreased need for sleep"
            details={note.sleepNeed}
            checked={note.sleepNeedCheckbox}
          />
          <Symptom
            label="Pressured speech/increased talking"
            details={note.speechAdditionalSymptomology}
            checked={note.speechAdditionalSymptomologyCheckbox}
          />
          <Symptom
            label="Flight of ideas/racing thoughts"
            details={note.racingThoughts}
            checked={note.racingThoughtsCheckbox}
          />
          <Symptom
            label="Distractible"
            details={note.distractible}
            checked={note.distractibleCheckbox}
          />
          <Symptom
            label="Increase in goal directed activity"
            details={note.goalActivity}
            checked={note.goalActivityCheckbox}
          />
          <Symptom
            label="Psychomotor agitation"
            details={note.psychomotorAgitation}
            checked={note.psychomotorAgitationCheckbox}
          />
          <Symptom
            label="Excessive risky behaviors"
            details={note.riskyBehaviors}
            checked={note.riskyBehaviorsCheckbox}
          />
          <Symptom
            label="Hypomania"
            details={note.hypomania}
            checked={note.hypomaniaCheckbox}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          PTSD & Schizophrenia
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Symptom
            label="Experienced/witnessed life threatening event"
            details={note.lifeThreateningEvent}
            checked={note.lifeThreateningEventCheckbox}
          />
          <Symptom
            label="Responded with fear/helplessness"
            details={note.fearHelplessness}
            checked={note.fearHelplessnessCheckbox}
          />
          <Symptom
            label="Recurrent distressing recollections of event"
            details={note.distressingRecollections}
            checked={note.distressingRecollectionsCheckbox}
          />
          <Symptom
            label="Recurrent dreams of event"
            details={note.recurrentDreams}
            checked={note.recurrentDreamsCheckbox}
          />
          <Symptom
            label="Feeling or acting as if event was reoccurring"
            details={note.eventReoccurring}
            checked={note.eventReoccurringCheckbox}
          />
          <Symptom
            label="Intense distress when exposed to aspect of trauma"
            details={note.intenseDistress}
            checked={note.intenseDistressCheckbox}
          />
          <Symptom
            label="Persistent avoidance of stimuli associated with event"
            details={note.avoidanceStimuli}
            checked={note.avoidanceStimuliCheckbox}
          />
          <Symptom
            label="Difficulty sleeping"
            details={note.difficultySleeping}
            checked={note.difficultySleepingCheckbox}
          />
          <Symptom
            label="Irritability/anger outbursts"
            details={note.irritabilityPTSD}
            checked={note.irritabilityCheckboxPTSD}
          />
          <Symptom
            label="Difficulty concentrating"
            details={note.difficultyConcentratingPTSD}
            checked={note.difficultyConcentratingCheckboxPTSD}
          />
          <Symptom
            label="Hyper vigilance/exaggerated startle response"
            details={note.hyperVigilance}
            checked={note.hyperVigilanceCheckbox}
          />
          <Symptom
            label="Delusions"
            details={note.delusionsPTSD}
            checked={note.delusionsCheckbox}
          />
          <Symptom
            label="Hallucinations"
            details={note.hallucinationsPTSD}
            checked={note.hallucinationsCheckboxPTSD}
          />
          <Symptom
            label="Disorganized speech"
            details={note.disorganizedSpeech}
            checked={note.disorganizedSpeechCheckbox}
          />
          <Symptom
            label="Grossly disorganization / catatonic"
            details={note.grossDisorganization}
            checked={note.grossDisorganizationCheckbox}
          />
          <Symptom
            label="Flat affect"
            details={note.flatAffect}
            checked={note.flatAffectCheckbox}
          />
          <Symptom
            label="Lack of speech"
            details={note.lackOfSpeech}
            checked={note.lackOfSpeechCheckbox}
          />
          <Symptom
            label="Lack of motivation"
            details={note.lackOfMotivation}
            checked={note.lackOfMotivationCheckbox}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* CAGE Screening Section */}
        <CAGEScreening note={note} />

        <Divider sx={{ my: 1 }} />

        <RiskAssessment note={note} />

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Screenings
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          PHQ-9 Depression Screening
        </Typography>
        {(() => {
          const hasAnyPHQ = Object.values(phqObj || {}).some(
            (v) => v !== null && v !== undefined && String(v).trim() !== "",
          );

          return (
            <>
              <TableContainer
                sx={{
                  mb: 1,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 90 }}>
                        Score
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 200 }}>
                        Frequency
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {phq9Items.map((text, idx) => {
                      const score = hasAnyPHQ ? phqScores.arr[idx] ?? "" : "";
                      return (
                        <TableRow key={`phq-${idx}`}>
                          <TableCell>
                            {idx + 1}. {text}
                          </TableCell>
                          <TableCell>{score}</TableCell>
                          <TableCell>
                            {score === "" ? "" : scoreToFreq(score)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 4,
                  mb: 1,
                }}
              >
                <Field
                  label="PHQ-9 Total"
                  value={
                    hasAnyPHQ
                      ? `${phqScores.total} (${phqSeverity(phqScores.total)})`
                      : ""
                  }
                />
                <Field
                  label="Difficulty"
                  value={hasAnyPHQ ? phqScores.diff || "" : ""}
                />
              </Box>
            </>
          );
        })()}

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}
        >
          GAD-7 Anxiety Screening
        </Typography>
        {(() => {
          const hasAnyGAD = Object.values(gadObj || {}).some(
            (v) => v !== null && v !== undefined && String(v).trim() !== "",
          );

          return (
            <>
              <TableContainer
                sx={{
                  mb: 1,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 90 }}>
                        Score
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 200 }}>
                        Frequency
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gad7Items.map((text, idx) => {
                      const score = hasAnyGAD ? gadScores.arr[idx] ?? "" : "";
                      return (
                        <TableRow key={`gad-${idx}`}>
                          <TableCell>
                            {idx + 1}. {text}
                          </TableCell>
                          <TableCell>{score}</TableCell>
                          <TableCell>
                            {score === "" ? "" : scoreToFreq(score)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 4,
                  mb: 1,
                }}
              >
                <Field
                  label="GAD-7 Total"
                  value={
                    hasAnyGAD
                      ? `${gadScores.total} (${gadSeverity(gadScores.total)})`
                      : ""
                  }
                />
                <Field
                  label="Difficulty"
                  value={hasAnyGAD ? gadScores.diff || "" : ""}
                />
              </Box>
            </>
          );
        })()}

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Optional Assessments (GAIN-SS)
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          <Field label="IDScr Past Month" value={note.idScrPastMonth} />
          <Field label="IDScr Past 90 Days" value={note.idScrPast90Days} />
          <Field label="IDScr Past Year" value={note.idScrPastYear} />
          <Field label="IDScr Ever" value={note.idScrEver} />
          <Field label="EDScr Past Month" value={note.edScrPastMonth} />
          <Field label="EDScr Past 90 Days" value={note.edScrPast90Days} />
          <Field label="EDScr Past Year" value={note.edScrPastYear} />
          <Field label="EDScr Ever" value={note.edScrEver} />
          <Field label="SDScr Past Month" value={note.sdScrPastMonth} />
          <Field label="SDScr Past 90 Days" value={note.sdScrPast90Days} />
          <Field label="SDScr Past Year" value={note.sdScrPastYear} />
          <Field label="SDScr Ever" value={note.sdScrEver} />
          <Field label="CVScr Past Month" value={note.cvScrPastMonth} />
          <Field label="CVScr Past 90 Days" value={note.cvScrPast90Days} />
          <Field label="CVScr Past Year" value={note.cvScrPastYear} />
          <Field label="CVScr Ever" value={note.cvScrEver} />
          <Field label="TDScr Past Month" value={note.tdScrPastMonth} />
          <Field label="TDScr Past 90 Days" value={note.tdScrPast90Days} />
          <Field label="TDScr Past Year" value={note.tdScrPastYear} />
          <Field label="TDScr Ever" value={note.tdScrEver} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          WHODAS
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            mb: 1,
          }}
        >
          {Array.from({ length: 12 }, (_, i) => `s${i + 1}`).map((k) => (
            <Field key={k} label={k.toUpperCase()} value={note[k]} />
          ))}
          <Field label="Total Item Score (/48)" value={note.totalItemScore} />
          <Field label="Total Percentage (%)" value={note.totalPercentage} />
          <Field
            label="Screened positive = a score of 1 or greater"
            value={note.brief ? "Yes" : ""}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Client Needs
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Field
            label="Assessment of client needs"
            value={splitList(note.clientNeeds)}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Clinical Summary
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Field label="Summary" value={note.clinicalSummary} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Recommendations for further services that are medically necessary
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Field label="Further Services" value={note.furtherServices} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Provisional diagnostic hypothesis
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Field label="Hypothesis" value={note.diagnosticHypothesis} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Diagnosis
        </Typography>
        {(() => {
          const diagnoses =
            Array.isArray(note?.dsm5Diagnosis) && note.dsm5Diagnosis.length > 0
              ? note.dsm5Diagnosis
              : [{}];

          return diagnoses.map((dx, idx) => (
            <Box
              key={idx}
              mb={1.5}
              sx={{ pl: 1, borderLeft: "3px solid #e0e0e0" }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                DSM-5 Diagnosis {idx + 1}
              </Typography>

              {/* Checkboxes Row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 4,
                  mb: 4,
                }}
              >
                <Field
                  label="Principal Diagnosis"
                  value={dx?.principalDiagnosis ? "Yes" : "No"}
                />
                <Field
                  label="Provisional Diagnosis"
                  value={dx?.provisionalDiagnosis ? "Yes" : "No"}
                />
                <Field
                  label="Diagnosis"
                  value={dx?.diagnosis ? "Yes" : "No"}
                />
              </Box>

              {/* Other Disorders - Checkbox */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 4,
                mb: 4,
              }}>
                <Field
                  label="Other Specified/Unspecified Disorders"
                  value={dx?.otherDisorders ? "Yes" : "No"}
                />
                <Field
                  label="Medical"
                  value={dx?.medical ? "Yes" : "No"}
                />
              </Box>

              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                DSM-5
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 4,
                  mb: 4,
                }}
              >
                <Field label="DSM-5 Code" value={dx?.dsM5} />
                <Field label="DSM-5 Detail" value={dx?.dsM5Detail} />
              </Box>

              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                ICD-10
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 4,
                  mb: 4,
                }}
              >
                <Field label="ICD-10 Code" value={dx?.icD10} />
                <Field label="ICD-10 Detail" value={dx?.icD10Detail} />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field label="Specifier" value={dx?.specifier} />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field
                  label="Specifier Explain"
                  value={dx?.specifierExplain}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field label="Subtype" value={dx?.subtype} />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field label="Subtype Explain" value={dx?.subtypeExplain} />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field label="Severity" value={dx?.severity} />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field
                  label="Severity Explain"
                  value={dx?.severityExplain}
                />
              </Box>
              <Box sx={{ mb: 4 }}>
                <Field label="Other Factors" value={dx?.otherFactors} />
              </Box>
            </Box>
          ));
        })()}

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Supportive Assessment(s) / Functional Assessment(s) and Screening(s)
        </Typography>
        {(() => {
          const supportive =
            Array.isArray(note?.supportiveAssessment) &&
              note.supportiveAssessment.length > 0
              ? note.supportiveAssessment
              : [{}];

          return supportive.map((sa, idx) => (
            <Box
              key={idx}
              mb={1.5}
              sx={{ pl: 1, borderLeft: "3px solid #e0e0e0" }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                Supportive Assessment {idx + 1}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 4,
                  mb: 4,
                }}
              >
                <Field
                  label="Date"
                  value={
                    sa?.date ? new Date(sa.date).toLocaleDateString() : ""
                  }
                />
                <Field label="Name" value={sa?.name} />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Field label="Description" value={sa?.description} />
              </Box>
              <Box sx={{ mb: 4 }}>
                <Field label="Notes" value={sa?.notes} />
              </Box>
            </Box>
          ));
        })()}

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" gutterBottom color="primary">
          Signatures
        </Typography>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: "bold",
                  width: "30%",
                }}
              >
                Supervised Clinician:
              </td>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {sigDisplay(
                  meta?.supervisedClinicianMsg,
                  meta?.supervisedDate || note?.supervisedDate,
                )}
              </td>
            </tr>

            <tr>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: "bold",
                }}
              >
                Intern Signature:
              </td>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {sigDisplay(
                  meta?.internMsg,
                  meta?.staffsignatureDATE || note?.staffsignatureDATE,
                )}
              </td>
            </tr>

            <tr>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: "bold",
                }}
              >
                Provider Signature:
              </td>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {sigDisplay(
                  meta?.providerMsg,
                  meta?.providerDate || note?.providerDate,
                )}
              </td>
            </tr>

            <tr>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: "bold",
                }}
              >
                Supervisor Signature:
              </td>
              <td
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {sigDisplay(
                  meta?.supervisorMsg,
                  meta?.supervisorSignatureDATE ||
                  note?.supervisorSignatureDATE,
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default AdultDiagnosticAssessmentPreview;